import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { PanelType } from './Graphs'
import { CockpitEntry, useUserSettings } from '../user/UserSettingsProvider'
import { useAuth } from '../user/Auth'

// `name` is the English source/fallback; `nameKey` is the i18n key (see the `panels` namespace
// in settings/locales/*/common.json) used to render/sort the panel picker in the user's language.
type PanelDef = { type: PanelType, id: number, name: string, nameKey: string }

export const PANEL_REGISTRY: PanelDef[] = [
    // Patent
    { type: 'Patent', id: 1, name: 'Overview Patents', nameKey: 'panels.overview-patents' },
    { type: 'Patent', id: 2, name: 'Active patent changes', nameKey: 'panels.active-patent-changes' },
    { type: 'Patent', id: 3, name: 'Patent usage', nameKey: 'panels.patent-usage' },
    { type: 'Patent', id: 4, name: 'IP status (patents)', nameKey: 'panels.ip-status-patents' },
    { type: 'Patent', id: 5, name: 'Family scores (patents)', nameKey: 'panels.family-scores-patents' },
    { type: 'Patent', id: 6, name: 'Prio expiries (first filings)', nameKey: 'panels.prio-expiries' },
    { type: 'Patent', id: 8, name: 'Yearly Patent Costs', nameKey: 'panels.yearly-patent-costs' },
    { type: 'Patent', id: 9, name: 'Patent Costs per Family', nameKey: 'panels.patent-costs-per-family' },
    { type: 'Patent', id: 10, name: 'Countries histogram (patents)', nameKey: 'panels.countries-histogram-patents' },
    { type: 'Patent', id: 11, name: 'Tags (patents)', nameKey: 'panels.tags-patents' },
    { type: 'Patent', id: 12, name: 'Forecast / Scenario (patents)', nameKey: 'panels.forecast-scenario-patents' },
    { type: 'Patent', id: 13, name: 'Radar Chart (patents)', nameKey: 'panels.radar-chart-patents' },
    // Trademark
    { type: 'Trademark', id: 1, name: 'Overview Trademarks', nameKey: 'panels.overview-trademarks' },
    { type: 'Trademark', id: 2, name: 'Active changes (trademarks)', nameKey: 'panels.active-changes-trademarks' },
    { type: 'Trademark', id: 3, name: 'Trademark usage', nameKey: 'panels.trademark-usage' },
    { type: 'Trademark', id: 4, name: 'Product protection (trademarks)', nameKey: 'panels.product-protection-trademarks' },
    { type: 'Trademark', id: 5, name: 'Countries (trademarks)', nameKey: 'panels.countries-trademarks' },
    { type: 'Trademark', id: 6, name: 'Yearly Trademark Costs', nameKey: 'panels.yearly-trademark-costs' },
    { type: 'Trademark', id: 7, name: 'Trademark Costs per Family', nameKey: 'panels.trademark-costs-per-family' },
    { type: 'Trademark', id: 8, name: 'Trademarks Pipeline', nameKey: 'panels.trademarks-pipeline' },
    // Design
    { type: 'Design', id: 1, name: 'Overview Designs', nameKey: 'panels.overview-designs' },
    { type: 'Design', id: 2, name: 'Countries (designs)', nameKey: 'panels.countries-designs' },
    // Product
    { type: 'Product', id: 1, name: 'Overview Products', nameKey: 'panels.overview-products' },
    { type: 'Product', id: 2, name: 'Product protection', nameKey: 'panels.product-protection' },
    // Patent monitoring
    { type: 'PatentMonitoring', id: 1, name: 'Monitoring overview', nameKey: 'panels.monitoring-overview' },
    // Inventions
    { type: 'Inventions', id: 1, name: 'Overview Inventions', nameKey: 'panels.overview-inventions' },
    // IP Management
    { type: 'IpManagement', id: 1, name: 'Tasks pipeline', nameKey: 'panels.tasks-pipeline' },
    { type: 'IpManagement', id: 2, name: 'Yearly IP Management Costs', nameKey: 'panels.yearly-ip-management-costs' },
    { type: 'IpManagement', id: 3, name: 'Total forecast', nameKey: 'panels.total-forecast' },
    { type: 'IpManagement', id: 4, name: 'Total overview', nameKey: 'panels.total-overview' },
    // Multiple (shared across cockpits)
    { type: 'Multiple', id: 1, name: 'Protected income', nameKey: 'panels.protected-income' },
    { type: 'Multiple', id: 2, name: 'Renewals pipeline', nameKey: 'panels.renewals-pipeline' },
    { type: 'Multiple', id: 3, name: 'Next tasks', nameKey: 'panels.next-tasks' },
    { type: 'Multiple', id: 4, name: 'Recommendations', nameKey: 'panels.recommendations' },
]

export const keyOf = (t: PanelType, id: number) => `${t}/${id}`

export const PANEL_DRAG_TYPE = 'cockpit-panel'

type CockpitPanelsContextType = {
    isVisible: (type: PanelType, id: number) => boolean
    getOrder: (type: PanelType, id: number) => number
    movePanel: (srcKey: string, dstKey: string, position: 'before' | 'after') => void
}

const CockpitPanelsContext = createContext<CockpitPanelsContextType>({
    isVisible: () => true,
    getOrder: () => 0,
    movePanel: () => { /* default no-op */ },
})

export function useCockpitPanel() {
    return useContext(CockpitPanelsContext)
}

// localStorage caches the layout per (realm, cockpit) for an instant first paint before the backend
// settings load. Backend `user_settings.cockpit_config` (nested by realm) is the source of truth.
const orderKeyOf = (realm: string, t: PanelType) => `cockpit-panels-order-${realm}-${t}`
const hiddenKeyOf = (realm: string, t: PanelType) => `cockpit-panels-hidden-${realm}-${t}`
// Pre-branch (non-realm) keys — read once to migrate an existing user's layout into their realm.
const legacyOrderKeyOf = (t: PanelType) => `cockpit-panels-order-${t}`
const legacyHiddenKeyOf = (t: PanelType) => `cockpit-panels-hidden-${t}`

function readEntry(orderKey: string, hiddenKey: string): CockpitEntry | null {
    try {
        const rawOrder = localStorage.getItem(orderKey)
        const rawHidden = localStorage.getItem(hiddenKey)
        if (rawOrder === null && rawHidden === null) return null
        return {
            order: rawOrder ? JSON.parse(rawOrder) : [],
            hidden: rawHidden ? JSON.parse(rawHidden) : [],
        }
    } catch { return null }
}

const readLocalEntry = (realm: string, cockpitType: PanelType) =>
    readEntry(orderKeyOf(realm, cockpitType), hiddenKeyOf(realm, cockpitType))
const readLegacyEntry = (cockpitType: PanelType) =>
    readEntry(legacyOrderKeyOf(cockpitType), legacyHiddenKeyOf(cockpitType))

function writeLocalEntry(realm: string, cockpitType: PanelType, entry: CockpitEntry) {
    try {
        localStorage.setItem(orderKeyOf(realm, cockpitType), JSON.stringify(entry.order))
        localStorage.setItem(hiddenKeyOf(realm, cockpitType), JSON.stringify(entry.hidden))
    } catch (e) { void e }
}

export function CockpitPanelsProvider({ cockpitType, children }: { cockpitType: PanelType, children: React.ReactNode }) {
    const { t } = useTranslation()
    const { team: realm } = useAuth()
    const { userSettings, cockpitConfig, saveCockpitConfig } = useUserSettings()

    // Effective layout: backend config (already sliced to the current realm) wins; fall back to the
    // realm-scoped cache for instant paint, then a pre-branch layout, then empty (registry defaults).
    const entry = useMemo<CockpitEntry>(
        () => cockpitConfig[cockpitType]
            ?? (realm ? readLocalEntry(realm, cockpitType) : null)
            ?? readLegacyEntry(cockpitType)
            ?? { order: [], hidden: [] },
        [cockpitConfig, cockpitType, realm],
    )

    // One-time migration of a pre-branch (non-realm) layout into the current realm's backend config.
    useEffect(() => {
        if (userSettings && realm && !cockpitConfig[cockpitType]) {
            const legacy = readLegacyEntry(cockpitType)
            if (legacy) saveCockpitConfig(cockpitType, legacy)
        }
    }, [userSettings, realm, cockpitConfig, cockpitType, saveCockpitConfig])

    // Keep the realm-scoped cache in sync with the backend so the next first paint matches the
    // persisted layout for this realm instead of flashing the defaults.
    useEffect(() => {
        const backendEntry = cockpitConfig[cockpitType]
        if (realm && backendEntry) writeLocalEntry(realm, cockpitType, backendEntry)
    }, [cockpitConfig, cockpitType, realm])

    const relevantKeys = useMemo(() =>
        PANEL_REGISTRY
            .filter(p => p.type === cockpitType || p.type === 'Multiple')
            .map(p => keyOf(p.type, p.id)),
        [cockpitType])

    const effectiveOrder = useMemo(() => {
        const known = new Set(entry.order.filter(k => relevantKeys.includes(k)))
        const head = entry.order.filter(k => known.has(k))
        const tail = relevantKeys.filter(k => !known.has(k))
        return [...head, ...tail]
    }, [entry.order, relevantKeys])

    const hidden = useMemo(() => new Set(entry.hidden), [entry.hidden])

    const persist = useCallback((next: CockpitEntry) => {
        if (realm) writeLocalEntry(realm, cockpitType, next) // realm-scoped cache for instant paint
        saveCockpitConfig(cockpitType, next)                 // durable, debounced backend write
    }, [realm, cockpitType, saveCockpitConfig])

    const isVisible = useCallback(
        (type: PanelType, id: number) => !hidden.has(keyOf(type, id)),
        [hidden],
    )
    const getOrder = useCallback(
        (type: PanelType, id: number) => {
            const idx = effectiveOrder.indexOf(keyOf(type, id))
            return idx === -1 ? effectiveOrder.length : idx
        },
        [effectiveOrder],
    )
    const movePanel = useCallback(
        (srcKey: string, dstKey: string, position: 'before' | 'after') => {
            if (srcKey === dstKey) return
            const next = effectiveOrder.filter(k => k !== srcKey)
            const dstIdx = next.indexOf(dstKey)
            if (dstIdx === -1) return
            const insertAt = position === 'before' ? dstIdx : dstIdx + 1
            next.splice(insertAt, 0, srcKey)
            persist({ order: next, hidden: [...hidden] })
        },
        [effectiveOrder, hidden, persist],
    )

    const toggle = (type: PanelType, id: number) => {
        const k = keyOf(type, id)
        const next = new Set(hidden)
        if (next.has(k)) next.delete(k); else next.add(k)
        persist({ order: effectiveOrder, hidden: [...next] })
    }

    const [menu, setMenu] = useState<{ x: number, y: number } | null>(null)
    const onContextMenu = (e: React.MouseEvent) => {
        e.preventDefault()
        const menuWidth = 260
        const menuHeight = 420
        setMenu({
            x: Math.min(e.clientX, window.innerWidth - menuWidth - 8),
            y: Math.min(e.clientY, window.innerHeight - menuHeight - 8),
        })
    }

    const cockpitPanels = PANEL_REGISTRY
        .filter(p => p.type === cockpitType)
        .slice()
        .sort((a, b) => t(a.nameKey).localeCompare(t(b.nameKey)))
    const multiplePanels = PANEL_REGISTRY
        .filter(p => p.type === 'Multiple')
        .slice()
        .sort((a, b) => t(a.nameKey).localeCompare(t(b.nameKey)))

    return (
        <CockpitPanelsContext.Provider value={{ isVisible, getOrder, movePanel }}>
            <div onContextMenu={onContextMenu} className="h-full">
                {children}
            </div>
            {menu && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setMenu(null)}
                        onContextMenu={e => { e.preventDefault(); setMenu(null) }}
                    />
                    <div
                        className="fixed bg-white shadow-lg rounded-md border border-pcx-300 py-1 z-50 min-w-[16rem] max-h-[80vh] overflow-auto text-sm"
                        style={{ left: menu.x, top: menu.y }}
                    >
                        <div className="px-3 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">{cockpitType}</div>
                        {cockpitPanels.map(p => (
                            <label key={keyOf(p.type, p.id)} className="flex items-center gap-2 px-3 py-1 hover:bg-pcx-100 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isVisible(p.type, p.id)}
                                    onChange={() => toggle(p.type, p.id)}
                                    className="form-checkbox"
                                />
                                <span>{t(p.nameKey)}</span>
                            </label>
                        ))}
                        {multiplePanels.length > 0 && cockpitPanels.length > 0 && (
                            <div className="border-t border-pcx-200 my-1" />
                        )}
                        {multiplePanels.length > 0 && (
                            <div className="px-3 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">Multiple</div>
                        )}
                        {multiplePanels.map(p => (
                            <label key={keyOf(p.type, p.id)} className="flex items-center gap-2 px-3 py-1 hover:bg-pcx-100 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isVisible(p.type, p.id)}
                                    onChange={() => toggle(p.type, p.id)}
                                    className="form-checkbox"
                                />
                                <span>{t(p.nameKey)}</span>
                            </label>
                        ))}
                    </div>
                </>
            )}
        </CockpitPanelsContext.Provider>
    )
}
